/**
 * BrightPath - Budgets API Client
 * BP-04-04: Expenses Frontend
 */

import apiClient from '@/core/lib/api-client';
import type {
  Budget,
  BudgetCreate,
  BudgetUpdate,
  BudgetListParams,
  BudgetStatus,
  BudgetSummary,
  BudgetTrend,
} from '../types';

// Note: apiClient already has /api/v1 as base URL
const BASE_URL = '';

/**
 * Get family budgets
 */
export async function getBudgets(
  familyId: string,
  params?: BudgetListParams
): Promise<Budget[]> {
  const searchParams = new URLSearchParams();
  
  if (params) {
    if (params.year) searchParams.set('year', params.year.toString());
    if (params.month) searchParams.set('month', params.month.toString());
    if (params.category) searchParams.set('category', params.category);
  }

  const query = searchParams.toString();
  return apiClient.get<Budget[]>(
    `${BASE_URL}/families/${familyId}/budgets${query ? `?${query}` : ''}`
  );
}

/**
 * Get a single budget by ID
 */
export async function getBudget(budgetId: string): Promise<Budget> {
  return apiClient.get<Budget>(`${BASE_URL}/budgets/${budgetId}`);
}

/**
 * Create a new budget
 */
export async function createBudget(
  familyId: string,
  data: BudgetCreate
): Promise<Budget> {
  return apiClient.post<Budget>(`${BASE_URL}/families/${familyId}/budgets`, data);
}

/**
 * Update an existing budget
 */
export async function updateBudget(
  budgetId: string,
  data: BudgetUpdate
): Promise<Budget> {
  return apiClient.patch<Budget>(`${BASE_URL}/budgets/${budgetId}`, data);
}

/**
 * Delete a budget
 */
export async function deleteBudget(budgetId: string): Promise<void> {
  return apiClient.delete(`${BASE_URL}/budgets/${budgetId}`);
}

/**
 * Get budget status (budget vs actual spending)
 */
export async function getBudgetStatus(
  familyId: string,
  params?: {
    year?: number;
    month?: number;
  }
): Promise<BudgetStatus[]> {
  const searchParams = new URLSearchParams();
  
  if (params) {
    if (params.year) searchParams.set('year', params.year.toString());
    if (params.month) searchParams.set('month', params.month.toString());
  }

  const query = searchParams.toString();
  return apiClient.get<BudgetStatus[]>(
    `${BASE_URL}/families/${familyId}/budgets/status${query ? `?${query}` : ''}`
  );
}

/**
 * Get budget summary for a period
 */
export async function getBudgetSummary(
  familyId: string,
  params?: {
    year?: number;
    month?: number;
  }
): Promise<BudgetSummary> {
  const searchParams = new URLSearchParams();
  
  if (params) {
    if (params.year) searchParams.set('year', params.year.toString());
    if (params.month) searchParams.set('month', params.month.toString());
  }

  const query = searchParams.toString();
  return apiClient.get<BudgetSummary>(
    `${BASE_URL}/families/${familyId}/budgets/summary${query ? `?${query}` : ''}`
  );
}

/**
 * Get budget trends over time
 */
export async function getBudgetTrends(
  familyId: string,
  params?: {
    months?: number; // Number of months to include
  }
): Promise<BudgetTrend[]> {
  const searchParams = new URLSearchParams();
  
  if (params?.months) {
    searchParams.set('months', params.months.toString());
  }

  const query = searchParams.toString();
  return apiClient.get<BudgetTrend[]>(
    `${BASE_URL}/families/${familyId}/budgets/trends${query ? `?${query}` : ''}`
  );
}

/**
 * Copy budgets from one month to another
 */
export async function copyBudgets(
  familyId: string,
  fromYear: number,
  fromMonth: number,
  toYear: number,
  toMonth: number
): Promise<Budget[]> {
  return apiClient.post<Budget[]>(`${BASE_URL}/families/${familyId}/budgets/copy`, {
    from_year: fromYear,
    from_month: fromMonth,
    to_year: toYear,
    to_month: toMonth,
  });
}

export const budgetsApi = {
  getBudgets,
  getBudget,
  createBudget,
  updateBudget,
  deleteBudget,
  getBudgetStatus,
  getBudgetSummary,
  getBudgetTrends,
  copyBudgets,
};
